import React, { useState, useCallback } from "react";
import { MONTHS_LONG, DAYS_SHORT, toYMD, fromYMD, localToday, sameDay, addDays, buildCalendarGrid } from "../../utils/dateUtils";

interface Props {
  selected: string;
  onChange: (ymd: string) => void;
}

export function Calendar({ selected, onChange }: Props) {
  const TODAY   = localToday();
  const minDate = addDays(TODAY, 1);
  const maxDate = addDays(TODAY, 30);

  const initDate = fromYMD(selected) ?? minDate;
  const [viewYear,  setViewYear]  = useState(initDate.getFullYear());
  const [viewMonth, setViewMonth] = useState(initDate.getMonth());
  const [animDir,   setAnimDir]   = useState<"left" | "right" | null>(null);

  const cells   = buildCalendarGrid(viewYear, viewMonth);
  const selDate = fromYMD(selected);

  const prevMonth = useCallback(() => {
    if (new Date(viewYear, viewMonth, 1) <= new Date(minDate.getFullYear(), minDate.getMonth(), 1)) return;
    setAnimDir("right");
    setViewMonth(m => { if (m === 0) { setViewYear(y => y - 1); return 11; } return m - 1; });
    setTimeout(() => setAnimDir(null), 220);
  }, [viewYear, viewMonth, minDate]);

  const nextMonth = useCallback(() => {
    if (new Date(viewYear, viewMonth, 1) >= new Date(maxDate.getFullYear(), maxDate.getMonth(), 1)) return;
    setAnimDir("left");
    setViewMonth(m => { if (m === 11) { setViewYear(y => y + 1); return 0; } return m + 1; });
    setTimeout(() => setAnimDir(null), 220);
  }, [viewYear, viewMonth, maxDate]);

  const canPrev = new Date(viewYear, viewMonth, 1) > new Date(minDate.getFullYear(), minDate.getMonth(), 1);
  const canNext = new Date(viewYear, viewMonth, 1) < new Date(maxDate.getFullYear(), maxDate.getMonth(), 1);

  const isDisabled = (d: Date) => d < minDate || d > maxDate;
  const isSelected = (d: Date) => !!selDate && sameDay(d, selDate);
  const isToday    = (d: Date) => sameDay(d, TODAY);
  const isWeekend  = (d: Date) => d.getDay() === 0 || d.getDay() === 6;

  const handleDay = (d: Date) => {
    if (!isDisabled(d)) onChange(toYMD(d));
  };

  const NavBtn = ({ onClick, disabled, children }: { onClick: () => void; disabled: boolean; children: React.ReactNode }) => (
    <button type="button" onClick={onClick} disabled={disabled}
      style={{ width:36, height:36, borderRadius:10, border:"1.5px solid var(--border)", background:disabled?"var(--surface2)":"var(--surface)", cursor:disabled?"not-allowed":"pointer", display:"flex", alignItems:"center", justifyContent:"center", transition:"all 0.18s", color:disabled?"var(--text3)":"var(--text)" }}
      onMouseEnter={e => { if (!disabled) (e.currentTarget as HTMLElement).style.background = "var(--indigo-light)"; }}
      onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = disabled ? "var(--surface2)" : "var(--surface)"; }}>
      {children}
    </button>
  );

  return (
    <div>
      {/* Month nav */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:18 }}>
        <NavBtn onClick={prevMonth} disabled={!canPrev}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="15 18 9 12 15 6"/></svg>
        </NavBtn>
        <span style={{ fontFamily:"var(--font-display)", fontWeight:700, fontSize:16, color:"var(--text)" }}>
          {MONTHS_LONG[viewMonth]} {viewYear}
        </span>
        <NavBtn onClick={nextMonth} disabled={!canNext}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="9 18 15 12 9 6"/></svg>
        </NavBtn>
      </div>

      {/* Day headers */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", marginBottom:6 }}>
        {DAYS_SHORT.map((d, i) => (
          <div key={d} style={{ textAlign:"center", fontSize:11, fontWeight:700, letterSpacing:"0.05em", padding:"4px 0", color: i===0||i===6 ? "var(--brand)" : "var(--text3)" }}>{d}</div>
        ))}
      </div>

      {/* Grid */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", gap:3, transition:"opacity 0.18s, transform 0.18s", opacity:animDir?0:1, transform:animDir==="left"?"translateX(-8px)":animDir==="right"?"translateX(8px)":"none" }}>
        {cells.map((d, idx) => {
          if (!d) return <div key={idx} />;
          const disabled_  = isDisabled(d);
          const selected_  = isSelected(d);
          const today_     = isToday(d);
          const weekend_   = isWeekend(d);

          return (
            <button key={idx} type="button" onClick={() => handleDay(d)} disabled={disabled_}
              style={{ aspectRatio:"1", borderRadius:9, border:selected_?"2px solid var(--indigo)":today_?"1.5px dashed var(--indigo-mid)":"2px solid transparent", background:selected_?"var(--indigo)":"transparent", color:selected_?"#fff":disabled_?"var(--text3)":weekend_?"var(--brand)":"var(--text)", fontFamily:"var(--font-display)", fontWeight:selected_||today_?700:400, fontSize:13, cursor:disabled_?"not-allowed":"pointer", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:2, transition:"background 0.15s, border-color 0.15s, box-shadow 0.15s", boxShadow:selected_?"0 3px 10px rgba(79,70,229,0.3)":"none", opacity:disabled_?0.3:1, userSelect:"none" }}
              onMouseEnter={e => { if (!disabled_ && !selected_) { const el = e.currentTarget as HTMLElement; el.style.background = "var(--indigo-light)"; el.style.borderColor = "var(--indigo-mid)"; } }}
              onMouseLeave={e => { if (!selected_) { const el = e.currentTarget as HTMLElement; el.style.background = "transparent"; el.style.borderColor = today_ ? "var(--indigo-mid)" : "transparent"; } }}>
              {d.getDate()}
              {today_ && !selected_ && <span style={{ width:4, height:4, borderRadius:"50%", background:"var(--indigo)", display:"block" }} />}
            </button>
          );
        })}
      </div>

      {/* Legend */}
      <div style={{ display:"flex", gap:16, marginTop:14, flexWrap:"wrap" }}>
        {[
          { bg:"var(--indigo)",   border:"none",                            label:"Selected" },
          { bg:"transparent",     border:"1.5px dashed var(--indigo-mid)", label:"Today" },
          { bg:"transparent",     border:"2px solid transparent",           label:"Unavailable", dim:true },
        ].map((l, i) => (
          <div key={i} style={{ display:"flex", alignItems:"center", gap:5, opacity: (l as any).dim ? 0.4 : 1 }}>
            <div style={{ width:13, height:13, borderRadius:4, background:l.bg, border:l.border||"none" }} />
            <span style={{ fontSize:11, color:"var(--text3)" }}>{l.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
